import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { RunePicker } from "../components/RunePicker";
import { ContinueReading } from "../components/ContinueReading";
import { ReadingActions } from "../components/ReadingActions";
import { MarkdownText } from "../components/MarkdownText";
import { CopyReadingButton } from "../components/CopyReadingButton";
import { DeleteReadingButton } from "../components/DeleteReadingButton";
import { markReadingCompleted } from "../components/ReviewPrompt";
import { MoonStar, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { notifyQuotaExhausted } from "../lib/quota";

export default function Rune() {
  const { user, loading } = useAuth();
  const [question, setQuestion] = useState("");
  const [phase, setPhase] = useState("form");
  const [session, setSession] = useState(null);
  const [cards, setCards] = useState([]);
  const [text, setText] = useState("");
  const [readingId, setReadingId] = useState(null);
  const [error, setError] = useState("");

  const RESUME_KEY = "arcana-resume-rune";
  const saveSnapshot = () => {
    try {
      sessionStorage.setItem(RESUME_KEY, JSON.stringify({ session, cards, text, readingId, question }));
    } catch {}
  };
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(RESUME_KEY);
      if (!raw) return;
      sessionStorage.removeItem(RESUME_KEY);
      const s = JSON.parse(raw);
      if (!s?.session || !s?.readingId) return;
      setSession(s.session);
      setCards(s.cards || []);
      setText(s.text || "");
      setReadingId(s.readingId);
      setQuestion(s.question || "");
      setPhase("done");
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    setError("");
    try {
      const { data } = await api.post("/draw-sessions", { deck: "rune", spread: "tre", question });
      setSession(data);
      setPhase("pick");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "quota_esaurita") {
        notifyQuotaExhausted();
      } else {
        setError(typeof d === "string" ? d : "Qualcosa è andato storto. Riprova.");
      }
    }
  };

  const beginInterpretation = async (sessionId) => {
    setText("");
    setCards([]);
    setPhase("reading");
    try {
      const res = await fetch(`${API}/draw-sessions/${sessionId}/interpret`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "Qualcosa è andato storto. Riprova.");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "meta") { setCards(ev.cards); setReadingId(ev.reading_id); }
          else if (ev.type === "delta") setText((t) => t + ev.content);
          else if (ev.type === "error") throw new Error(ev.content);
        }
      }
      setPhase("done");
      markReadingCompleted("rune");
    } catch (e) {
      setError(e.message);
      setPhase("form");
    }
  };

  const reset = () => {
    setPhase("form");
    setSession(null);
    setQuestion("");
    setCards([]);
    setText("");
    setError("");
  };

  if (loading) return null;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="rune-login-required">
        <div className="mystic-card p-10">
          <MoonStar className="w-10 h-10 text-amber-400/60 mx-auto mb-5" />
          <h1 className="font-display text-3xl text-amber-100 mb-4">Il Tiro da 3</h1>
          <p className="text-purple-200/70 text-sm mb-8">
            Per consultare le Rune è necessario accedere o creare un account: le tue letture resteranno
            custodite nella tua Area Cliente.
          </p>
          <Link to="/accedi?redirect=/rune" data-testid="rune-login-btn" className="gold-btn inline-block px-8 py-3 text-sm">
            Accedi o registrati
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-16 sm:py-20" data-testid="rune-page">
      <div className="text-center mb-12">
        <p className="eyebrow">Rune</p>
        <h1 className="font-display text-4xl sm:text-5xl text-amber-100 mt-4">Il Tiro da 3</h1>
        <p className="text-purple-200/70 mt-4 text-sm max-w-xl mx-auto">
          Concentrati sulla tua domanda: le Rune nel sacchetto verranno mescolate e ne sceglierai tre,
          una alla volta, senza poterle vedere. Passato, presente e possibile futuro.
        </p>
      </div>

      {phase === "form" && (
        <div className="mystic-card p-7 sm:p-9 space-y-6" data-testid="rune-form">
          <div>
            <p className="eyebrow mb-4">La tua domanda</p>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Scrivi qui la tua domanda…"
              data-testid="rune-question-input"
              className="input-dark min-h-[110px] resize-y"
            />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="rune-error">{error}</p>}
          <button
            onClick={start}
            disabled={question.trim().length < 5}
            data-testid="rune-start-btn"
            className="gold-btn w-full py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" /> Mescola il sacchetto
          </button>
          <p className="text-xs text-purple-300/50 text-center leading-relaxed">
            La lettura ha finalità di riflessione e orientamento personale. Il futuro può essere
            modificato dalle tue azioni e dalle tue scelte.
          </p>
        </div>
      )}

      {phase === "pick" && session && (
        <div className="mystic-card p-5 sm:p-7" data-testid="rune-pick">
          <p className="text-sm text-purple-300/60 italic mb-3 text-center">“{question}”</p>
          <p className="eyebrow text-center mb-2">Il sacchetto è stato mescolato</p>
          <p className="text-xs text-purple-300/60 text-center mb-6 max-w-lg mx-auto leading-relaxed">
            Scegli tre pietre, una alla volta, lasciandoti guidare dall'intuito. Ogni pietra nasconde
            una Runa precisa, stabilita dal mescolamento: la scoprirai solo alla fine.
          </p>
          <RunePicker
            sessionId={session.id}
            totalCards={session.total_cards}
            spread={session.count}
            onComplete={() => beginInterpretation(session.id)}
          />
        </div>
      )}

      {(phase === "reading" || phase === "done") && (
        <div className="space-y-8">
          <div className="mystic-card p-7 sm:p-9" data-testid="rune-result">
            <p className="text-sm text-purple-300/60 italic mb-6">“{question}”</p>
            <div className="grid grid-cols-3 gap-3 max-w-lg mx-auto" data-testid="rune-cards">
              {cards.map((c, i) => (
                <div key={i} className="rounded-lg bg-[#0f0617] border border-amber-500/20 p-4 text-center" data-testid={`rune-card-${i}`}>
                  <div className={`inline-block text-4xl sm:text-5xl text-amber-300 ${c.reversed ? "rotate-180" : ""}`}>
                    {c.title || <span className="inline-block w-9 h-9 rounded-full border-2 border-amber-400/70" />}
                  </div>
                  <p className="font-display text-amber-100 text-sm mt-2 leading-tight">{c.name}</p>
                  <p className="text-[10px] uppercase tracking-wider text-purple-300/50 mt-1.5">{c.position}</p>
                  <span className={`inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full border ${
                    c.reversed ? "border-fuchsia-500/40 text-fuchsia-300" : "border-amber-500/40 text-amber-300"
                  }`}>
                    {c.reversed ? "Ribaltata" : "Diritta"}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-amber-500/15">
              <p className="eyebrow mb-4">L'interpretazione</p>
              <div className="text-purple-100/85 leading-loose text-sm sm:text-base whitespace-pre-line" data-testid="rune-interpretation">
                <MarkdownText text={text} />
                {phase === "reading" && <span className="inline-block w-2 h-4 bg-amber-400/70 ml-1 animate-pulse" />}
              </div>
              {phase === "done" && text && (
                <div className="mt-4 flex justify-center gap-2 flex-wrap">
                  <CopyReadingButton text={text} testid="copy-reading-rune" />
                  {readingId && (
                    <DeleteReadingButton testid="delete-reading-rune" onDelete={async () => {
                      await api.delete(`/readings/${readingId}`);
                      toast.success("Testo eliminato.");
                      sessionStorage.removeItem(RESUME_KEY);
                      window.location.reload();
                    }} />
                  )}
                </div>
              )}
              {phase === "reading" && cards.length === 0 && (
                <p className="text-purple-300/50 text-sm">Le Rune stanno parlando…</p>
              )}
            </div>
            {phase === "done" && session && (
              <ContinueReading
                sessionId={session.id}
                testid="rune"
                renderPicker={(s, onDone) => (
                  <RunePicker key={s.id} sessionId={s.id} totalCards={s.total_cards} spread={s.count} onComplete={onDone} />
                )}
              />
            )}
            {phase === "done" && (
              <ReadingActions
                readingId={readingId}
                assistantTo={`/assistente?tipo=lettura&id=${readingId}`}
                onReset={reset}
                onAssistant={saveSnapshot}
                resetLabel="Nuovo tiro"
                testid="rune"
              />
            )}
          </div>
          {phase === "done" && (
            <p className="text-xs text-purple-300/50 text-center leading-relaxed">
              La lettura ha finalità di riflessione e orientamento personale. Il futuro può essere
              modificato dalle tue azioni e dalle tue scelte.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
